<script>
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import { _ } from '../lib/i18n.js';
  import { statsApi, customerPortalApi } from '../stores/api.js';
  import ClosureDaysPanel from './ClosureDaysPanel.svelte';
  import {
    FileText, ArrowRight, AlertCircle, User, Eye, ThumbsUp, CheckCircle, Clock
  } from 'lucide-svelte';

  let customer = null;
  let stats = null;
  let loading = true;
  let error = null;

  onMount(async () => {
    await loadData();
  });

  async function loadData() {
    loading = true;
    error = null;
    try {
      const [customerData, statsData] = await Promise.all([
        customerPortalApi.getMe(),
        statsApi.get()
      ]);
      customer = customerData;
      stats = statsData;
      console.log('[CustomerDashboard] Loaded data:', { customer, stats });
    } catch (err) {
      console.error('[CustomerDashboard] Error loading data:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  function getDisplayName() {
    if (!customer) return '';
    if (customer.company_name) return customer.company_name;
    return [customer.name, customer.surname].filter(Boolean).join(' ') || $_('customer.defaultName');
  }

  function formatNumber(num) {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('it-IT').format(num);
  }

  // Status colors matching the main Proposals page
  const statusColors = {
    sent: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    seen: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    customer_approved: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400',
    confirmed: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
  };

  const statusIcons = {
    sent: Clock,
    seen: Eye,
    customer_approved: ThumbsUp,
    confirmed: CheckCircle
  };
</script>

<div class="max-w-7xl mx-auto">
  {#if loading}
    <div class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
    </div>
  {:else if error}
    <div class="card-elevated p-8 text-center">
      <AlertCircle size={48} class="mx-auto text-red-500 mb-4" />
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">{$_('common.error')}</h3>
      <p class="text-gray-500 dark:text-gray-400">{error}</p>
      <button on:click={loadData} class="btn btn-primary mt-4">{$_('common.retry')}</button>
    </div>
  {:else}
    <!--
      Welcome header, built out of the same parts as every other panel here: a
      card surface, the border tokens, and the accent kept to an edge and a
      small tile. It used to be a full-bleed primary-500 field with white text
      on it, which is 1.41:1 — not a shade to soften but a pairing to drop. The
      text now takes --color-text-heading / --color-text-body over the card
      surface: 17.7:1 and 10.3:1 in light, 18.2:1 and 9.1:1 in dark, so the
      theme it is read in no longer decides whether it is legible.

      The accent edge is an inline style, not `border-l-4 border-l-primary-500`:
      `html:not(.dark) .card-elevated` sets the whole border at specificity
      0-2-0 and outranks a single-class utility, so those classes would have
      drawn the edge in dark mode and quietly dropped it in light.
    -->
    <div class="card-elevated p-6 mb-8" style="border-left: 4px solid var(--color-primary);">
      <div class="flex items-center space-x-4">
        <div
          class="w-16 h-16 border-2 border-primary-500 flex items-center justify-center flex-shrink-0"
          style="background: rgba(0, 240, 255, 0.1);"
        >
          <User size={32} class="text-primary-700 dark:text-primary-400" />
        </div>
        <div class="min-w-0">
          <h2 class="text-2xl font-bold text-heading">{$_('customerDashboard.welcome', { values: { name: getDisplayName() } })}</h2>
          <p class="text-body mt-1">{$_('customerDashboard.subtitle')}</p>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <!-- Sent -->
      <div class="card-elevated p-4">
        <div class="flex items-center justify-between mb-2">
          <div class="w-10 h-10 {statusColors.sent} rounded-lg flex items-center justify-center">
            <Clock size={20} />
          </div>
        </div>
        <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {formatNumber(stats?.proposalsByStatus?.sent || 0)}
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400">{$_('proposals.status.sent')}</div>
      </div>

      <!-- Seen -->
      <div class="card-elevated p-4">
        <div class="flex items-center justify-between mb-2">
          <div class="w-10 h-10 {statusColors.seen} rounded-lg flex items-center justify-center">
            <Eye size={20} />
          </div>
        </div>
        <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {formatNumber(stats?.proposalsByStatus?.seen || 0)}
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400">{$_('proposals.status.seen')}</div>
      </div>

      <!-- Customer Approved -->
      <div class="card-elevated p-4">
        <div class="flex items-center justify-between mb-2">
          <div class="w-10 h-10 {statusColors.customer_approved} rounded-lg flex items-center justify-center">
            <ThumbsUp size={20} />
          </div>
        </div>
        <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {formatNumber(stats?.proposalsByStatus?.customer_approved || 0)}
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400">{$_('proposals.status.customer_approved')}</div>
      </div>

      <!-- Confirmed -->
      <div class="card-elevated p-4">
        <div class="flex items-center justify-between mb-2">
          <div class="w-10 h-10 {statusColors.confirmed} rounded-lg flex items-center justify-center">
            <CheckCircle size={20} />
          </div>
        </div>
        <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {formatNumber(stats?.proposalsByStatus?.confirmed || 0)}
        </div>
        <div class="text-sm text-gray-600 dark:text-gray-400">{$_('proposals.status.confirmed')}</div>
      </div>
    </div>

    <!-- Upcoming company closures, company-wide: the same rows the staff home
         shows, since non_working_days holds nothing customer-specific. -->
    <div class="mb-8">
      <ClosureDaysPanel />
    </div>

    <!-- Quick Actions -->
    <div class="card-elevated p-6">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
            <FileText class="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">{$_('customerDashboard.proposals.title')}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">{$_('customerDashboard.proposals.subtitle')}</p>
          </div>
        </div>
        <a
          href="/proposals"
          use:link
          class="btn btn-primary flex items-center space-x-2"
        >
          <span>{$_('customerDashboard.viewAllProposals')}</span>
          <ArrowRight size={16} />
        </a>
      </div>

      <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
        <p class="text-gray-600 dark:text-gray-400">
          {$_('customerDashboard.totalProposals', { values: { count: stats?.proposals || 0 } })}
        </p>
        {#if (stats?.proposalsByStatus?.sent || 0) + (stats?.proposalsByStatus?.seen || 0) > 0}
          <p class="text-primary-600 dark:text-primary-400 font-medium mt-2">
            {$_('customerDashboard.pendingApproval', {
              values: {
                count: (stats?.proposalsByStatus?.sent || 0) + (stats?.proposalsByStatus?.seen || 0)
              }
            })}
          </p>
        {/if}
      </div>
    </div>
  {/if}
</div>
